import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Inventory, InventoryCategory, InventoryGroup, CreateInventoryRequest } from '../../types/inventory';
import { getCategories, getGroups } from '../../services/inventoryApi';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: CreateInventoryRequest) => Promise<void>;
    inventory?: Inventory;
}

export default function CreateEditInventoryModal({ isOpen, onClose, onSubmit, inventory }: Props) {
    const [categories, setCategories] = useState<InventoryCategory[]>([]);
    const [groups, setGroups] = useState<InventoryGroup[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isConsumable, setIsConsumable] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchCategories();
            if (inventory) {
                setIsConsumable(inventory.is_consummable);
                if (inventory.group?.category) {
                    setSelectedCategory(inventory.group.category.id.toString());
                    fetchGroups(inventory.group.category.id);
                }
            }
        }
    }, [isOpen, inventory]);

    const fetchCategories = async () => {
        try {
            const response = await getCategories();
            setCategories(response.data.data);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const fetchGroups = async (categoryId: number) => {
        try {
            const response = await getGroups(categoryId);
            setGroups(response.data.data);
        } catch (error) {
            console.error('Failed to fetch groups:', error);
        }
    };

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const categoryId = e.target.value;
        setSelectedCategory(categoryId);
        if (categoryId) {
            fetchGroups(parseInt(categoryId));
        } else {
            setGroups([]);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const formData = new FormData(e.currentTarget);
            const data: CreateInventoryRequest = {
                inventory_group_id: parseInt(formData.get('inventory_group_id') as string),
                name: formData.get('name') as string,
                description: formData.get('description') as string,
                unit_price: parseFloat(formData.get('unit_price') as string),
                is_consummable: isConsumable,
                sku: formData.get('sku') as string || undefined,
                serial_number: formData.get('serial_number') as string || undefined,
                po_number: formData.get('po_number') as string || undefined,
                invoice_number: formData.get('invoice_number') as string || undefined
            };

            if (isConsumable) {
                data.consummable = {
                    quantity: parseFloat(formData.get('quantity') as string),
                    part_name: formData.get('part_name') as string || undefined,
                    part_number: formData.get('part_number') as string || undefined
                };
            }

            await onSubmit(data);
            onClose();
        } catch (error) {
            setError('Failed to save inventory item');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">
                        {inventory ? 'Edit' : 'Create'} Inventory Item
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                        <X size={24} />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 text-red-700 bg-red-100 rounded-lg">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Category</label>
                            <select
                                name="category"
                                className="mt-1 block w-full rounded-md border-gray-300"
                                value={selectedCategory}
                                onChange={handleCategoryChange}
                                required
                            >
                                <option value="">Select Category</option>
                                {categories.map(category => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Group</label>
                            <select
                                name="inventory_group_id"
                                className="mt-1 block w-full rounded-md border-gray-300"
                                defaultValue={inventory?.inventory_group_id}
                                required
                            >
                                <option value="">Select Group</option>
                                {groups.map(group => (
                                    <option key={group.id} value={group.id}>
                                        {group.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Name</label>
                            <input
                                type="text"
                                name="name"
                                defaultValue={inventory?.name}
                                className="mt-1 block w-full rounded-md border-gray-300"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Unit Price</label>
                            <input
                                type="number"
                                name="unit_price"
                                step="0.01"
                                defaultValue={inventory?.unit_price}
                                className="mt-1 block w-full rounded-md border-gray-300"
                                required
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Description</label>
                            <textarea
                                name="description"
                                rows={3}
                                defaultValue={inventory?.description}
                                className="mt-1 block w-full rounded-md border-gray-300"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">SKU</label>
                            <input
                                type="text"
                                name="sku"
                                defaultValue={inventory?.sku || ''}
                                className="mt-1 block w-full rounded-md border-gray-300"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Serial Number</label>
                            <input
                                type="text"
                                name="serial_number"
                                defaultValue={inventory?.serial_number || ''}
                                className="mt-1 block w-full rounded-md border-gray-300"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">PO Number</label>
                            <input
                                type="text"
                                name="po_number"
                                defaultValue={inventory?.po_number || ''}
                                className="mt-1 block w-full rounded-md border-gray-300"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Invoice Number</label>
                            <input
                                type="text"
                                name="invoice_number"
                                defaultValue={inventory?.invoice_number || ''}
                                className="mt-1 block w-full rounded-md border-gray-300"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="is_consummable"
                                    checked={isConsumable}
                                    onChange={(e) => setIsConsumable(e.target.checked)}
                                    className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                                />
                                <label htmlFor="is_consummable" className="ml-2 text-sm font-medium text-gray-700">
                                    Is Consumable
                                </label>
                            </div>
                        </div>

                        {isConsumable && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Quantity</label>
                                    <input
                                        type="number"
                                        name="quantity"
                                        step="0.01"
                                        defaultValue={inventory?.consummable?.quantity}
                                        className="mt-1 block w-full rounded-md border-gray-300"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Part Name</label>
                                    <input
                                        type="text"
                                        name="part_name"
                                        defaultValue={inventory?.consummable?.part_name || ''}
                                        className="mt-1 block w-full rounded-md border-gray-300"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Part Number</label>
                                    <input
                                        type="text"
                                        name="part_number"
                                        defaultValue={inventory?.consummable?.part_number || ''}
                                        className="mt-1 block w-full rounded-md border-gray-300"
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
} 